import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { api, AUTH, IS_ADMIN, logout } from "../lib/api";
import { ago, dateTime } from "../lib/format";
import { ErrorText, Field } from "./ui";

// Account security cards shared by the app ("Your account → Security") and
// the admin console: authenticator app, recovery codes, signed-in devices.
// Kept free of app-only imports so the admin bundle stays separate.

export interface SecurityInfo {
  mfa: { enabled: boolean; enabled_at: string | null; recovery_codes_left: number; this_session_used_app: boolean; required_by: string[] };
  sessions: { id: string; current: boolean; user_agent: string; ip_prefix: string; created_at: string; last_used_at: string; expires_at: string; used_authenticator_app: boolean }[];
  idle_days: number;
  email: string;
  has_password: boolean;
}

export function useSecurity() {
  return useQuery({ queryKey: ["account-security"], queryFn: () => api<SecurityInfo>(`${AUTH}/security`) });
}

// "Chrome on Windows" from a user-agent string, good enough to recognise a device.
function deviceName(ua: string) {
  const browser = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "A browser";
  const os = /iPhone|iPad/.test(ua) ? "iPhone/iPad" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} on ${os}` : browser;
}

/** AccountSecurity is "Your account → Security": authenticator app, recovery codes, signed-in devices. */
export function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const text = `SVENEX recovery codes\nEach code works once.\n\n${codes.join("\n")}\n`;
  return (
    <div className="recovery">
      <p><b>Save these recovery codes now.</b> If you lose your phone, each one lets you sign in once. They won't be shown again.</p>
      <ul className="recovery-codes">{codes.map((c) => <li key={c}><code>{c}</code></li>)}</ul>
      <div className="inline">
        <button type="button" className="secondary" onClick={() => navigator.clipboard?.writeText(text)}>Copy</button>
        <a className="button-link" download="svenex-recovery-codes.txt" href={`data:text/plain;charset=utf-8,${encodeURIComponent(text)}`}>Download</a>
        <button type="button" onClick={onDone}>I've saved them</button>
      </div>
    </div>
  );
}

export function AuthenticatorCard({ info }: { info: SecurityInfo }) {
  const qc = useQueryClient();
  const [setup, setSetup] = useState<{ otpauth_uri: string; key: string } | null>(null);
  const [qr, setQr] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [mode, setMode] = useState<"" | "disable" | "codes">("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const m = info.mfa;
  const refresh = () => { qc.invalidateQueries({ queryKey: ["account-security"] }); qc.invalidateQueries({ queryKey: ["me"] }); qc.invalidateQueries({ queryKey: ["admin-me"] }); };
  useEffect(() => {
    if (setup) QRCode.toDataURL(setup.otpauth_uri, { margin: 1, width: 200 }).then(setQr, () => setQr(""));
  }, [setup]);
  async function run(fn: () => Promise<void>) {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError(e); } finally { setBusy(false); }
  }
  if (codes) return <section className="card"><h2>Authenticator app</h2><RecoveryCodes codes={codes} onDone={() => { setCodes(null); setMode(""); refresh(); }} /></section>;
  return (
    <section className="card">
      <div className="inline"><h2 style={{ margin: 0 }}>Authenticator app</h2>
        {m.enabled ? <span className="tag status-approved">On</span> : <span className="tag">Off</span>}</div>
      <p className="muted small">With an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…), signing in needs your password and a code from your phone, so a stolen password alone can't get in.</p>
      {m.required_by.length > 0 && <p className="notice small">{m.required_by.join(", ")} require{m.required_by.length === 1 ? "s" : ""} an authenticator app.</p>}
      {m.enabled && !m.this_session_used_app && <p className="notice warn small">You signed in on this device before turning the app on. Sign out and back in to use it here.</p>}
      <ErrorText error={error} />

      {!m.enabled && !setup && <button disabled={busy} onClick={() => run(async () => setSetup(await api(`${AUTH}/mfa/setup`, { method: "POST" })))}>Set up authenticator app</button>}
      {!m.enabled && setup && (
        <div className="mfa-setup">
          <ol>
            <li>Open your authenticator app and add an account by scanning this code.</li>
            <li>Can't scan? Enter this key instead: <code className="totp-key">{setup.key}</code></li>
            <li>Type the 6-digit code the app shows.</li>
          </ol>
          {qr && <img className="totp-qr" src={qr} alt="QR code to add SVENEX to your authenticator app" width={200} height={200} />}
          <form className="form" onSubmit={(e) => { e.preventDefault(); const code = String(new FormData(e.currentTarget).get("code") ?? ""); run(async () => { const r = await api<{ recovery_codes: string[] }>(`${AUTH}/mfa/enable`, { method: "POST", json: { code } }); setSetup(null); setCodes(r.recovery_codes); }); }}>
            <Field label="Code from the app"><input name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required autoFocus /></Field>
            <div className="inline"><button type="submit" disabled={busy}>Turn on</button><button type="button" className="link" onClick={() => setSetup(null)}>Cancel</button></div>
          </form>
        </div>
      )}

      {m.enabled && (
        <>
          <p className="small">On since {m.enabled_at ? dateTime(m.enabled_at) : "—"}. Recovery codes left: <b>{m.recovery_codes_left}</b>{m.recovery_codes_left <= 3 && " — make new ones soon."}</p>
          {!mode && (
            <div className="inline">
              <button className="secondary" onClick={() => setMode("codes")}>New recovery codes</button>
              {m.required_by.length === 0 && !IS_ADMIN && <button className="link danger-link" onClick={() => setMode("disable")}>Turn off</button>}
            </div>
          )}
          {mode && (
            <form className="form" onSubmit={(e) => {
              e.preventDefault();
              const d = new FormData(e.currentTarget);
              const json = { password: String(d.get("password") ?? ""), code: String(d.get("code") ?? "") };
              run(async () => {
                if (mode === "disable") { await api("/v1/auth/mfa/disable", { method: "POST", json }); setMode(""); refresh(); }
                else { const r = await api<{ recovery_codes: string[] }>(`${AUTH}/mfa/recovery-codes`, { method: "POST", json }); setCodes(r.recovery_codes); }
              });
            }}>
              <p className="small">{mode === "disable" ? "To turn the app off, confirm it's you." : "Making new codes cancels the old ones. Confirm it's you."}</p>
              <Field label="Password"><input name="password" type="password" autoComplete="current-password" maxLength={256} /></Field>
              <Field label="Code from the app (or a recovery code)"><input name="code" autoComplete="one-time-code" maxLength={12} required /></Field>
              <div className="inline"><button type="submit" disabled={busy} className={mode === "disable" ? "danger" : ""}>{mode === "disable" ? "Turn off" : "Make new codes"}</button><button type="button" className="link" onClick={() => setMode("")}>Cancel</button></div>
            </form>
          )}
        </>
      )}
    </section>
  );
}

export function SessionsCard({ info }: { info: SecurityInfo }) {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["account-security"] });
  const others = info.sessions.filter((s) => !s.current);
  return (
    <section className="card">
      <div className="inline"><h2 style={{ margin: 0 }}>Where you're signed in</h2><span className="spacer" />
        {others.length > 0 && <button className="secondary" onClick={async () => { setError(null); try { await api(`${AUTH}/sessions/end-others`, { method: "POST" }); refresh(); } catch (e) { setError(e); } }}>Sign out everywhere else</button>}</div>
      <p className="muted small">{IS_ADMIN ? "Admin sessions end after 30 minutes without use, and always 12 hours after signing in." : `Sessions end after ${info.idle_days} days without use.`} Don't recognise one? Sign it out and change your password.</p>
      <ErrorText error={error} />
      <ul className="session-list">
        {info.sessions.map((s) => (
          <li key={s.id}>
            <div><b>{deviceName(s.user_agent)}</b>{s.current && <span className="tag status-approved">This device</span>}{s.used_authenticator_app && <span className="tag">App code</span>}
              <div className="muted small">Last active {ago(s.last_used_at)} · signed in {dateTime(s.created_at)}{s.ip_prefix && ` · network ${s.ip_prefix}`}</div></div>
            {s.current
              ? <button className="link" onClick={() => logout()}>Sign out</button>
              : <button className="link danger-link" onClick={async () => { setError(null); try { await api(`${AUTH}/sessions/${s.id}`, { method: "DELETE" }); refresh(); } catch (e) { setError(e); } }}>Sign out</button>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** MFAGate replaces a company's pages when it requires the app and this session didn't use it. */
